import { Context } from "@nuxt/types/app";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function (context: Context) {
  async function getSelectedFilesInfo(
    files: Array<File>,
    supportedTypes: Array<string> | string,
    maxSize: Record<string, any>,
    isTotalSize: boolean,
  ): Promise<Record<string, any>> {
    let totalSizeBytes = 0;

    // https://stackoverflow.com/a/47065508/9433691
    const result: Record<string, any> = await Promise.all(
      // https://stackoverflow.com/a/25333553/9433691
      (Array.from(files || {}) || []).map(async (file: File) => {
        const deviceOS = context.app.$ui.getDeviceOS();

        const fileList = file;
        let filePreview: string | Record<string, any> = "";
        let fileBlobURL = "";
        let fileData = null;

        // file name - remove vietnam character
        // https://stackoverflow.com/a/49901740/9433691
        const fileName = (file.name || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");
        const fileNameOriginal = file.name || "";

        // file type (image/jpeg, application/pdf, etc)
        const fileType = file.type;

        // file category (image, video, application, etc)
        const fileCategory = fileType.split("/")[0] ?? "image";

        // fileType is something like image/jpeg, application/pdf
        // convert to get the extension (.jpeg, .pdf)
        // const fileTypeExtension = this.getExtensionFromType(file.type);
        // https://stackoverflow.com/a/190878/9433691
        // file type extension
        const fileTypeExtension = `.${(
          fileName.split(".").pop() || ""
        ).toLowerCase()}`;

        // file size
        const fileSize = file.size / 1024; // convert to KB
        totalSizeBytes += file.size;

        try {
          const fileResult: Record<string, any> = await new Promise(
            (resolve, reject) => {
              if (file.name.lastIndexOf(".") <= 0) {
                return;
              }

              // validation for supportedTypes array type
              if (typeof supportedTypes === "object") {
                // check extension is supported or not
                // just in case user use 'all types' uploader
                if (
                  typeof supportedTypes !== "undefined" &&
                  supportedTypes.length &&
                  !supportedTypes.includes(fileTypeExtension)
                ) {
                  throw new Error(
                    `File is not supported. Please choose file with ${supportedTypes.join(
                      ", ",
                    )} extension`,
                  );
                }
              }

              // validation for supportedTypes === video/*
              if (supportedTypes === "video/*") {
                // check extension is supported or not
                // just in case user use 'all types' uploader
                const customSupportedFiles = [
                  ".mov",
                  ".mp4",
                  ".avi",
                  ".flv",
                  ".mkv",
                  ".wmv",
                ];
                if (
                  typeof supportedTypes !== "undefined" &&
                  supportedTypes.length &&
                  !customSupportedFiles.includes(fileTypeExtension)
                ) {
                  throw new Error(
                    `File is not supported. Please choose file with ${customSupportedFiles.join(
                      ", ",
                    )} extension`,
                  );
                }
              }

              // check size
              if (typeof maxSize !== "undefined") {
                const limitUnit = maxSize.unit;
                let sizeLimitBytes = maxSize.size;

                // convert to bytes from MB
                if (limitUnit.toUpperCase() === "MB") {
                  sizeLimitBytes =
                    context.app.$unit.getBytesFromMB(sizeLimitBytes);
                }

                // convert to bytes from KB
                if (limitUnit.toUpperCase() === "KB") {
                  sizeLimitBytes =
                    context.app.$unit.getBytesFromKB(sizeLimitBytes);
                }

                if (isTotalSize && totalSizeBytes > sizeLimitBytes) {
                  throw new Error(
                    `Total files uploaded must be smaller than ${maxSize.size} ${maxSize.unit}`,
                  );
                }

                if (!isTotalSize && file.size > sizeLimitBytes) {
                  throw new Error(
                    `Please choose file smaller than ${maxSize.size} ${maxSize.unit}`,
                  );
                }
              }

              const fr = new FileReader();
              fr.readAsDataURL(file);
              fr.onload = () => {
                resolve({
                  result: fr.result,
                  raw: file,
                });
              };

              fr.onerror = reject;
            },
          );
          filePreview = fileResult.result;
          fileData = fileResult.result.split(",").pop();
          const blobURL = URL.createObjectURL(file);
          fileBlobURL = blobURL;

          // process file for video
          if (fileType.includes("video")) {
            const fileResultVideo: Record<string, any> = await new Promise(
              (resolve, reject) => {
                const fr = new FileReader();
                fr.onload = () => {
                  // get screenshot
                  // https://gist.github.com/webolizzer/329f3713886f51750d587abc65374ea9
                  // eslint-disable-next-line no-undef
                  const blob = new Blob([fr.result as BlobPart], {
                    type: fileType,
                  });
                  const url = URL.createObjectURL(blob);
                  const video = document.createElement("video");
                  let imageBase64: string | null = null;

                  const snapImage = () => {
                    const canvas = document.createElement("canvas");
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;
                    // eslint-disable-next-line no-unused-expressions
                    canvas
                      .getContext("2d")
                      ?.drawImage(video, 0, 0, canvas.width, canvas.height);
                    const image = canvas.toDataURL();
                    const success = image.length > 100000;
                    imageBase64 = image;

                    if (success) {
                      // const img = document.getElementById(`${this.id}videoPreview${index}`);
                      // img.src = image;
                      URL.revokeObjectURL(url);
                    }

                    if (imageBase64 !== null) {
                      resolve({
                        preview: fr.result,
                        thumbnail: imageBase64,
                      });
                    }
                    return success;
                  };

                  const timeupdate = () => {
                    if (snapImage()) {
                      video.removeEventListener("timeupdate", timeupdate);
                      video.pause();
                    }
                  };

                  if (deviceOS === "iOS") {
                    if (snapImage()) {
                      video.removeEventListener("timeupdate", timeupdate);
                      video.remove();
                    }
                    if (timeupdate) timeupdate();
                  } else {
                    video.addEventListener("loadeddata", () => {
                      if (snapImage()) {
                        video.removeEventListener("timeupdate", timeupdate);
                        video.remove();
                      }
                    });
                    video.addEventListener("timeupdate", timeupdate);
                  }

                  video.preload = "metadata";
                  video.src = url;
                  // Load video in Safari / IE11
                  video.muted = true;
                  video.playsInline = true;
                  if (deviceOS !== "iOS") video.play();
                };
                fr.readAsArrayBuffer(file);

                fr.onerror = reject;
              },
            );

            filePreview = fileResultVideo;
            const videoBlobURL = URL.createObjectURL(file);
            const [, tmp2] = videoBlobURL.split("blob:");
            fileBlobURL = tmp2;
          }
        } catch (error) {
          throw new Error(error);
        }

        return {
          fileList,
          fileName,
          fileNameOriginal,
          fileType,
          fileTypeExtension,
          fileCategory,
          fileSize,
          filePreview,
          fileData,
          fileBlobURL,
        };
      }),
    );

    return result;
  }

  return {
    getSelectedFilesInfo,
  };
}
